import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { MenuProps } from "antd";
import { Menu } from "antd";

import { googleLogout } from "@react-oauth/google";

import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { removeUserInfo } from "../../features/user/userSlice";
import { removeInstance } from "../../features/instance/instanceSlice";
import { removeSelectionInfo } from "../../features/selection/selectionSlice";
import { clearAllSessions } from "../../utility/common/session-store";
import { updateMenuKey } from "../../features/settings/settingsSlice";
import { updateFolderPath } from "../../features/cloud/cloudSlice";
import { LangComp } from "../LangComp";
import { useAuth } from "../../utility/AuthContext";
import { HomeIcon } from "../../icons/homeIcon";
import { CloudIcon } from "../../icons/cloudIcon";
import { UsageIcon } from "../../icons/usageIcon";
import { BillingsIcon } from "../../icons/billingsIcon";
import { AccountIcon } from "../../icons/accountIcon";
import { DocsIcon } from "../../icons/docsIcon";

type MenuItem = Required<MenuProps>["items"][number];

export const MainMenu = () => {
  const { t } = useTranslation();
  const settingsState = useAppSelector((state: any) => state.settings);
  const [menuType, setMenuType] = useState<boolean>(true);
  const { isLoggedIn, setShowLoginModal, logout } = useAuth();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const toggleMenu = () => {
    setMenuType(!menuType);
  };

  const navigateLink = (menuKey: number, param: string) => {
    dispatch(updateMenuKey(menuKey));
    toggleMenu();
    navigate(param);
  };

  const instanceMenuItem = () => {
    dispatch(updateFolderPath(""));
    navigateLink(5, "/instances");
  };

  const mainMenuItems: MenuItem[] = [
    {
      key: 0,
      icon: <HomeIcon />,
      label: <>{t("Home")}</>,
      children: [
        {
          key: 1,
          label: (
            <div
              className="submenu-indent"
              onClick={() => navigateLink(1, "/")}
            >
              {t("Dashboard")}
            </div>
          ),
        },
        {
          key: 2,
          label: (
            <div
              className="submenu-indent"
              onClick={() => navigateLink(1, "/templates")}
            >
              {t("Templates")}
            </div>
          ),
        },
        {
          key: 3,
          label: (
            <div
              className="submenu-indent"
              onClick={() => {
                instanceMenuItem();
              }}
            >
              {t("Instances")}
            </div>
          ),
        },
      ],
    },
    {
      key: 4,
      icon: <CloudIcon />,
      label: <div onClick={() => navigateLink(4, "/cloud")}>{t("Cloud")}</div>,
    },
    // {
    //   key: 5,
    //   icon: (
    //     <svg
    //       xmlns="http://www.w3.org/2000/svg"
    //       width="20"
    //       height="20"
    //       viewBox="0 0 20 20"
    //       fill="none"
    //     >
    //       <path
    //         fill-rule="evenodd"
    //         clip-rule="evenodd"
    //         d="M10.7585 1.75C11.3894 1.75002 12.0345 1.86007 12.5277 2.257C13.056 2.68226 13.2584 3.3086 13.2585 3.99528V4.11328C13.733 4.30397 14.1523 4.57765 14.5028 4.93766C15.2518 5.70723 15.5964 6.77361 15.5965 7.97152V8.55827C15.5965 8.97238 15.2611 9.30769 14.847 9.30778C14.4328 9.30778 14.0967 8.97243 14.0967 8.55827V7.97152C14.0966 7.05443 13.8361 6.40295 13.4277 5.9834C13.1776 5.72656 12.8374 5.5212 12.3893 5.40153C12.3477 5.39488 12.3071 5.38544 12.2681 5.37223C12.0194 5.31675 11.7399 5.28597 11.4266 5.28597H8.63525C8.35869 5.28597 8.10929 5.3107 7.88493 5.35596C7.8237 5.37924 7.75892 5.39503 7.69124 5.40153C7.25577 5.52049 6.92584 5.72412 6.68294 5.97852C6.28329 6.39715 6.02709 7.04987 6.02702 7.97152V14.071C6.02703 14.9967 6.28347 15.6491 6.68213 16.0656C7.07564 16.4765 7.69902 16.7536 8.63281 16.7508H9.54346L9.61995 16.7549C9.99801 16.7933 10.2928 17.1122 10.293 17.5003C10.293 17.8886 9.99813 18.2082 9.61995 18.2466L9.54346 18.2507H8.63525C7.39475 18.2539 6.33958 17.8769 5.59814 17.1024C4.86161 16.3329 4.52638 15.267 4.52637 14.071V7.97152C4.52644 6.77816 4.86186 5.71303 5.59733 4.94255C5.95311 4.56987 6.38177 4.28909 6.86849 4.09701V3.99528C6.86853 3.31377 7.06453 2.68889 7.58464 2.26188C8.0728 1.86116 8.7122 1.75002 9.33512 1.75H10.7585ZM9.33512 3.25065C8.84743 3.25068 8.62829 3.34496 8.53597 3.42074C8.48744 3.4606 8.41015 3.54809 8.38135 3.7902C8.46506 3.78658 8.54973 3.78532 8.63525 3.78532H11.4266C11.5341 3.78532 11.6401 3.7887 11.7448 3.79427C11.7158 3.55402 11.6383 3.46694 11.5869 3.42562C11.488 3.34606 11.2583 3.25067 10.7585 3.25065H9.33512Z"
    //         fill="#282828"
    //       />
    //       <path
    //         fill-rule="evenodd"
    //         clip-rule="evenodd"
    //         d="M9.73714 9.10352C10.1267 9.06036 11.622 9.05502 13.2194 9.56983L13.4269 9.64144C14.4636 10.0222 15.4795 10.7497 16.0335 11.8249L16.1361 12.0397C16.3636 12.5594 16.501 13.1944 16.4038 13.8765C16.3098 14.5361 16.0028 15.1883 15.4329 15.7808C15.5753 15.9138 15.7278 16.0436 15.8927 16.1641L16.1556 16.3423L16.2183 16.387C16.5181 16.6206 16.5988 17.0478 16.3924 17.3766C16.1858 17.7056 15.7656 17.8189 15.4248 17.6501L15.3581 17.6126L15.1807 17.4971C14.8147 17.2487 14.4951 16.9708 14.2163 16.689C13.6641 16.967 13.1106 17.0802 12.5773 17.0576C11.8891 17.0285 11.2904 16.7773 10.8211 16.4587L10.6283 16.3187C9.67255 15.5779 9.14314 14.4452 8.95833 13.3564L8.92578 13.14C8.71099 11.4745 8.98801 10.0053 9.10156 9.63086L9.14225 9.52751C9.25243 9.29495 9.47622 9.13249 9.73714 9.10352ZM10.437 10.592C10.3599 11.1272 10.2908 11.9973 10.4134 12.9479L10.4378 13.1115C10.5764 13.9242 10.9613 14.6789 11.547 15.133L11.5479 15.1338C11.8542 15.3714 12.2328 15.5414 12.6408 15.5586C12.821 15.5662 13.0215 15.5424 13.2414 15.4715C13.0549 15.1836 12.9145 14.9289 12.819 14.7383C12.7665 14.6336 12.7267 14.5477 12.6994 14.486C12.6858 14.4553 12.6753 14.4302 12.6676 14.4119C12.6638 14.4028 12.661 14.3949 12.6587 14.3892C12.6575 14.3863 12.6562 14.3838 12.6554 14.3818C12.6551 14.381 12.6549 14.3801 12.6546 14.3794L12.6538 14.3786C12.654 14.3782 12.6681 14.3714 13.2992 14.1222L12.6538 14.377C12.5019 13.9917 12.691 13.5565 13.0762 13.4045C13.4607 13.2528 13.895 13.4406 14.0479 13.8244L14.0487 13.8252L14.0519 13.8333C14.0556 13.8422 14.0622 13.8573 14.0715 13.8781C14.0899 13.9198 14.1191 13.9842 14.1602 14.0661C14.23 14.2054 14.3329 14.3939 14.4694 14.6089C14.7538 14.2717 14.878 13.9494 14.9186 13.6649C14.9691 13.3107 14.9004 12.9572 14.7632 12.6436L14.7005 12.5117C14.3607 11.8523 13.6891 11.3366 12.915 11.0518L12.7588 10.998C11.8464 10.704 10.9777 10.6138 10.437 10.592Z"
    //         fill="#282828"
    //       />
    //     </svg>
    //   ),
    //   label: (
    //     <div onClick={() => navigateLink(7, "/esgReport")}>
    //       {t("ESG Report")}
    //     </div>
    //   ),
    // },
    // {
    //   key: 6,
    //   icon: <PartitionOutlined />,
    //   label: (
    //     <div onClick={() => navigateLink(6, "/instanceGroup")}>
    //       {t("Instance Group")}
    //     </div>
    //   ),
    // },
    {
      key: 7,
      label: <div className="menu-section-title">{t("Help")}</div>,
      type: "group",
      children: [
        {
          key: 8,
          icon: <DocsIcon />,
          label: (
            <div onClick={() => navigateLink(8, "/docs")}>{t("Docs")}</div>
          ),
        },
        // {
        //   key: 9,
        //   icon: (
        //     <svg
        //       xmlns="http://www.w3.org/2000/svg"
        //       width="20"
        //       height="20"
        //       viewBox="0 0 20 20"
        //       fill="none"
        //     >
        //       <path
        //         d="M8.05176 11.9577C8.46597 11.9577 8.80208 12.2938 8.80208 12.708V12.7487C8.80198 13.1628 8.46591 13.4982 8.05176 13.4982C7.63761 13.4982 7.30154 13.1628 7.30143 12.7487V12.708C7.30143 12.2938 7.63754 11.9577 8.05176 11.9577Z"
        //         fill="#282828"
        //       />
        //       <path
        //         d="M8.28288 6.53776C9.55337 6.53781 10.5884 7.56532 10.5884 8.84489C10.5884 9.75828 10.0601 10.3985 9.40592 10.7622L9.40674 10.763C9.16474 10.9006 9.06852 10.946 8.96647 11.021C8.89899 11.0706 8.84172 11.1265 8.77686 11.2415C8.57337 11.6022 8.11548 11.7298 7.75472 11.5264C7.39397 11.3229 7.26645 10.865 7.46989 10.5042C7.83164 9.86278 8.33044 9.64898 8.66536 9.4585L8.6735 9.45361L8.77767 9.39095C9.00002 9.23937 9.08854 9.06088 9.08854 8.84489C9.08852 8.3965 8.7277 8.03846 8.28288 8.03841C7.83431 8.03841 7.47723 8.3948 7.47721 8.84489C7.47721 9.2591 7.1411 9.59522 6.72689 9.59522C6.31268 9.59522 5.97656 9.2591 5.97656 8.84489C5.97658 7.56697 7.00529 6.53776 8.28288 6.53776Z"
        //         fill="#282828"
        //       />
        //       <path
        //         fill-rule="evenodd"
        //         clip-rule="evenodd"
        //         d="M11.2288 3.21094C12.4027 3.211 13.4118 3.63082 14.1235 4.39502C14.71 5.02478 15.0556 5.84713 15.1563 6.76237L16.7961 5.42122C17.5762 4.78247 18.7265 4.89752 19.3652 5.67676L19.366 5.67757L19.4604 5.80371C19.6668 6.10572 19.7787 6.46506 19.7778 6.83561L19.7786 6.83643L19.7689 13.1711C19.7657 14.1772 18.9498 14.9941 17.9403 14.9924H17.9386L17.9378 14.9932C17.5691 14.9927 17.2105 14.8803 16.9092 14.6733L16.7839 14.5789L15.1554 13.241C15.0541 14.1543 14.7091 14.9749 14.1235 15.6035C13.4116 16.3678 12.4023 16.7875 11.228 16.7876H5.56966C4.39524 16.7876 3.38648 16.3672 2.67497 15.6027C1.96915 14.8442 1.61133 13.8074 1.61133 12.6649V7.33366C1.61133 6.19011 1.971 5.153 2.67741 4.39502C3.38926 3.63122 4.39787 3.21098 5.56966 3.21094H11.2288ZM5.56966 4.71159C4.76956 4.71163 4.17437 4.98975 3.77523 5.41797C3.37059 5.85214 3.11198 6.50149 3.11198 7.33366V12.6649C3.11198 13.4981 3.36918 14.1477 3.77279 14.5814C4.17069 15.0089 4.76589 15.2869 5.56966 15.2869H11.228C12.0318 15.2869 12.6273 15.0083 13.0257 14.5806C13.4297 14.1468 13.6873 13.4977 13.6873 12.6649V11.742C13.6831 11.6933 13.682 11.6444 13.6873 11.5955V8.41113C13.6808 8.35394 13.6808 8.29636 13.6873 8.23942V7.33366C13.6873 6.50088 13.4296 5.85177 13.0257 5.41797C12.6274 4.99023 12.0321 4.71165 11.2288 4.71159H5.56966ZM18.2048 6.62728C18.0904 6.48827 17.8845 6.46804 17.7458 6.58171L15.188 8.67562V11.327L17.736 13.4193L17.7824 13.4518C17.8307 13.4792 17.8855 13.4933 17.9403 13.4933H17.9427L18.0078 13.4868C18.1554 13.457 18.2677 13.3265 18.2682 13.167L18.2788 6.83398V6.83236L18.2739 6.77783C18.2677 6.74172 18.255 6.70644 18.2365 6.67367L18.2048 6.62728Z"
        //         fill="#282828"
        //       />
        //     </svg>
        //   ),
        //   label: <div>{t("Tutorials")}</div>,
        // },
        {
          key: 10,
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M9.95606 12.7469C10.3703 12.7469 10.7064 13.083 10.7064 13.4972V13.5208C10.7063 13.935 10.3702 14.2703 9.95606 14.2703C9.54188 14.2703 9.20578 13.935 9.20573 13.5208V13.4972C9.20574 13.083 9.54185 12.7469 9.95606 12.7469Z"
                fill="#282828"
              />
              <path
                d="M10.1595 5.75391C11.6693 5.75391 12.8996 6.97487 12.8996 8.49479C12.8996 9.57431 12.2772 10.335 11.4844 10.7718C11.1913 10.9387 11.0003 11.0367 10.8488 11.173C10.7613 11.2518 10.7233 11.3114 10.7105 11.3781L10.7048 11.4481L10.7023 11.5254C10.6688 11.904 10.3532 12.2031 9.96501 12.2082C9.57675 12.2133 9.25321 11.9225 9.2098 11.5449L9.20492 11.4684L9.20736 11.3496C9.23515 10.768 9.52309 10.3481 9.84538 10.0581C10.153 9.78138 10.5479 9.57913 10.7511 9.46322L10.7609 9.45752L10.9123 9.36475C11.2408 9.13971 11.3989 8.85413 11.3989 8.49479C11.3989 7.80613 10.8437 7.25456 10.1595 7.25456C9.46937 7.25456 8.91927 7.80473 8.91927 8.49479C8.91924 8.90898 8.58314 9.24512 8.16895 9.24512C7.75475 9.24512 7.41865 8.90898 7.41862 8.49479C7.41862 6.97627 8.64098 5.75391 10.1595 5.75391Z"
                fill="#282828"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M10 1.75C14.5566 1.75 18.2503 5.44368 18.2503 10.0003C18.2503 14.5561 14.5567 18.2507 10 18.2507C5.4433 18.2507 1.74968 14.5561 1.74968 10.0003C1.74968 5.44368 5.44335 1.75 10 1.75ZM10 3.25065C6.27178 3.25065 3.25033 6.27211 3.25033 10.0003C3.25033 13.7278 6.27184 16.75 10 16.75C13.7282 16.75 16.7497 13.7277 16.7497 10.0003C16.7497 6.27211 13.7282 3.25065 10 3.25065Z"
                fill="#282828"
              />
            </svg>
          ),
          label: <div>{t("FAQ")}</div>,
        },
      ],
    },
    {
      key: 11,
      label: <div className="menu-section-title">{t("Account")}</div>,
      type: "group",
      children: [
        {
          key: 12,
          icon: <UsageIcon />,
          label: (
            <div onClick={() => navigateLink(3, "/usage")}>{t("Usage")}</div>
          ),
        },
        {
          key: 13,
          icon: <BillingsIcon />,
          label: (
            <div onClick={() => navigateLink(3, "/billings")}>
              {t("Billings")}
            </div>
          ),
        },
        {
          key: 14,
          icon: <AccountIcon />,
          label: (
            <div onClick={() => navigateLink(2, "/account")}>
              {t("Profile Setting")}
            </div>
          ),
        },
      ],
    },
  ];

  const handleLogInLogOut = () => {
    if (isLoggedIn) {
      googleLogout();
      document.cookie =
        "g_state=; path=/; domain=.google.com; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      dispatch(removeUserInfo());
      dispatch(removeInstance());
      dispatch(removeSelectionInfo());
      clearAllSessions();
      logout();
      if (location.pathname === "/") {
        window.location.reload();
      } else {
        navigate("/", { replace: true });
      }
    }
  };

  return (
    <div className="main-menu">
      <aside>
        <div
          className={
            menuType ? "main-menu-wrapper" : "main-menu-wrapper show-mobile"
          }
        >
          <Menu
            mode="inline"
            inlineIndent={8}
            defaultSelectedKeys={["1"]}
            defaultOpenKeys={["0"]}
            items={mainMenuItems}
          />
          <div className="bottom-menu">
            <LangComp />
            <div
              data-no-auth
              style={{ cursor: "pointer" }}
              onClick={() => handleLogInLogOut()}
            >
              {isLoggedIn ? t("Log Out") : t("Log In")}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};
